/** @module csi/view/_query/Level */
define([
	"dojo/_base/declare",
	"underscore",
	"csi",
	"csi/config/preferences",
	"csi/store/DataStoreNodes",
	"csi/store/GroupNodes",
	"csi/store/Nodes",
	"csi/xml"
], function(declare, _, csi, configPreferences, DataStoreNodes, GroupNodes, Nodes, xml) {

	const ROOT_LEVEL = "-1";

	const _Level = declare(null, {
		// Actions: actions available for items at this level
		//actions: null,

		// Function: access function to check availability of all actions
		//actionsAccessFunction: null,

		// Boolean: indicates if new actions are allowed even when grouped
		//bAllowNewActionsWhenGrouped: false,

		// Boolean: indicates if level should be expanded when first viewed (currently only configured for groups)
		//bAutoExpand: false,

		// Boolean: indicates if items at level can be selected for aggregation
		//bCanAggregate: false,

		// Boolean: indicates if client nodes should be counted as results
		//bOmitClientNodesFromCount: false,

		// Boolean: indicates if row actions should be rendered even if disabled
		//bRenderDisabledActions: false,

		// Boolean: indicates if level can be configured with custom view filters
		//bConfigurable: false,

		/** @property {boolean} bExportNoMore - only export what is currently in store rather than query for more results */
		//bExportNoMore: false,

		/** @property {boolean} bForMatrix - indicates query level is created for a matrix that can be filtered */
		//bForMatrix: false,

		// Boolean: indicates if results at this level result of first relationship (or if no relationship)
		//bMain: false,

		// Boolean: indicates if results are stored in a particular order
		//bOrdered: false,

		// Boolean: indicates if results are read-only at this level
		//bReadOnly: false,

		// Boolean: indicates if query for results relies on root node
		//bRelativeQuery: false,

		// Boolean: indicates if results of query are displayed as results in view (rather than a matrix query where results used for another purpose, such as columns in view)
		//bResults: false,

		// QueryLevel: next level in tree structure
		//childLevel: null,

		// Object: query object to filter store results on client
		//clientAdHocQuery: null,

		// Nodes: store at level solely used to provide Values store when configuring view filters
		//configurableStore: null,

		// Function(ctx, bConfigurable): custom function to override default method to create store from Relationship
		//createCustomStore: null,

		// String: css for level
		//css: null,

		// UserDefault[]: user defaults applied to level query; does not include those applied to ad-hoc query
		//enforcedUserDefaults: null,

		// Spec: indicates Spec used for grouping level (optional)
		//groupSpec: null,

		/** @property {Store} [groupDefaultsStore] - specifies a default set of values for grouped levels that will appear whether or not those values exist in the actual data */
		//groupDefaultsStore: null,

		/** @property {Object} [groupStoreClass] - class to use to create group store */
		groupStoreClass: GroupNodes,

		// function(ctx): custom function to test for children of given node in context - will override resultPath setting
		//hasChildrenTest: null,

		// String: level identifier ("0" - "N"); "-1" for root level
		id: null,

		/** @property {Spec[]} matrixSpecs - array of specs on level that represent matrices */
		//matrixSpecs: null,

		// String: name for level
		//name: null,

		// String: attribute containing results at this level
		//parentAttribute: null,

		/** @property {PathHints} [pathHints] - path hints for level */
		//pathHints: null,

		// _QueryMixin: access to _QueryMixin
		qm: null,

		// QueryReport: report definition for level (Table only)
		//report: null,

		// Object: parameters to pass to store, including dependency paths
		//requestParameters: null,

		// String: indicates path to results at this level if simple path
		//resultPath: null,

		// Rule[]: styling rules for level
		//rules: null,

		// Query: custom filter query to apply to store request
		//serverQuery: null,

		/** @property {Specs} specs - displayed specs at this level */
		specs: null,

		/** @property {boolean} skipEnforcedUserDefaults - indicates that query already applies enforced user defaults, so client does not have to */
		//skipEnforcedUserDefaults: false,

		/** @property {boolean} skipUnenforcedUserDefaults - indicates that unenforced user defaults should not be applied (as ad-hoc filters) */
		//skipUnenforcedUserDefaults: false,

		/** @property {SpecBuilder} specBuilder - spec builder with defaults set for level (mandatory for levels tied to hierarchy levels) */
		specBuilder: null,

		// Object: class used to create store (normally a derived class of Nodes)
		storeClass: Nodes,

		// Object: extra options passed to store when created
		//storeOptions: null,

		// String: type of node at level
		type: null,

		/** @property {string} typeSortAttribute - attribute storing sort order for displayed type */
		//typeSortAttribute: null,

		/** @property {Object.<string,any[]>} unenforcedQueryObject - main level ad-hoc query for user defaults */
		//unenforcedQueryObject: null,

		// Relationship: Relationship definition that queries for results at this level; if you want Relationship that generates children
		// of this level, then need to look at childLevel._rel
		//_rel: null,

		constructor: function(/*Object?*/params) {
			_.extend(this, params);

			if (this.bMain && (this.bCanAggregate == null)) {
				this.bCanAggregate = true;
			}
		},

		actionFilterFunc: function(/*Context*/ctx, /*QueryResult*/qrParent, /*Action*/action) {
			// summary: override this function to filter actions based on context or queryResult. Return false to exclude an action.
			return true;
		},

		addDependencyPaths: function(/*String[]?*/paths) {
			// summary: add dependency paths for level
			if (paths && paths.length) {
				if (this._hasCrewDependency()) {
					console.warn("Warning: Dependency Paths can be expensive when used with Dep.CrewUserAssignment or Dep.CrewRoleAssignment request parameters.");
				}

				var params = this.requestParameters = this.requestParameters || {};
				var dp = "Dep.Path";
				params[dp] = _.uniq(params[dp] ? _.union(params[dp], paths) : paths);
			}
		},

		/**
		 * Configure query level to use the given data store
		 * @param {string} dataStoreId 
		 * @param {cnl} sourceUrl 
		 * @param {Record<string, any>} [extraParams]
		 */
		configureForDataStore(dataStoreId, sourceUrl, extraParams) {
			const {requestParameters} = this;

			this.storeClass = DataStoreNodes;
			this.requestParameters = {
				...requestParameters,
				...extraParams,
				DataStoreId: dataStoreId,
				OverrideDefaultSorting: true,
				SourceUrl: sourceUrl,
			};
		},

		createStore: function(/*Context*/ctx, /*Boolean*/bConfigurable, /*Object?*/params) {
			// summary: create store given current context -- override if not using Relationship
			let store = this.createCustomStore?.(ctx, bConfigurable);
			if (!store) {
				// otherwise, create store from Relationship if there is one
				if (!this._rel) {
					return null;
				}

				if (bConfigurable || !this.bRelativeQuery || ctx.getNode()) {
					// querySpecStore only really needed for main level
					var storeOptions = _.extend({
						cachingStore: this.qm.cacheView,
						querySpecStore: this.qm.get("querySpecStore"),
						refreshTimestamp: this.qm.get("refreshTimestamp"),
						requestParameters: this.requestParameters
					}, params);

					if (this.storeOptions) {
						_.extend(storeOptions, this.storeOptions);
					}

					store = Nodes.createFromRelationship(this._rel, ctx, storeOptions, null, this.storeClass);

					// add query filters to store from custom view and those hard-coded for view
					if (!bConfigurable) {
						var levelFilter = this.getFilter();
						if (levelFilter) {
							store.addFilterQuery(levelFilter.toQuery(store.createQuery()));
						}
					}

					if (this.serverQuery) {
						store.addFilterQuery(this.serverQuery);
					}

					// if view is fixed to display all results, then disable query accelerator
					if (this.qm.get("querySizeFixed")) {
						var directQuery = store.createQuery();
						directQuery.bDirect = true;
						store.addFilterQuery(directQuery);
					}

					// if store results are filtered by client, then turn off server filtering (we currently only support one or the other)
					if (this.clientAdHocQuery) {
						store.bSimpleQueryEngine = true;
					}

					// call extension point if there is one
					this.onCreateStore(store, bConfigurable, this);

					// add query for user defaults - only do so after store.filterQuery is fully configured, as user default filters
					// only apply if the same attributes are not used elsewhere in the query
					if (!bConfigurable) {
						this._applyUserDefaults(store.filterQuery);
					}
				} else if (this.bRelativeQuery) {
					// create empty store if no context for relative query
					store = new Nodes({
						cachingStore: this.qm.cacheView,
						data: []
					});
				}
			}

			if (store) {
				ctx.view.annotateViewStore(store);
			}

			return store;
		},

		getContext: function(/*String*/url, /*String*/parentUrl) {
			// summary: return context for item - nodes generated for client (group, report, action);
			// for now, do not apply read-only flag because it can affect both the headings (correct) and actions (incorrect)

			// the following IF statement is removed due to C8-3597. It was causing inconsistent behavior. When group is not expanded, then the context
			// parent URL is set. Once expanded, the parent URL attribute on the DOM object gets lost due to this if statement. The side effects of this is not clear
			// but because of causing inconsistency, it makes sense to remove it and fix problems raised by this change.
			// if (xml.isClientUrl(url)) {
			// parentUrl = null;
			// }

			return this.qm.getContext(url, parentUrl, parentUrl ? this.parentAttribute : null);
		},

		getDependencyPaths: function() {
			// summary: return list of dependency paths for level
			var paths = this.requestParameters && this.requestParameters["Dep.Path"];
			return paths ? _.uniq(paths) : null;
		},

		/**
		 * Return filter object for level; if at a group level, then ask for child level's filter
		 * as group levels have no specific filters in the custom view
		 * @return {LevelFilter}
		 */
		getFilter: function() {
			return this.groupSpec ? this.childLevel.getFilter() : this.qm.getQueryLevelFilter(this);
		},

		getLevelIndex: function() {
			// summary: return index of level that maps to Relationship index - grouping always applies to first level which is 0
			return this.groupSpec ? 0 : Number(this.id);
		},

		/**
		 * Populate query for level that includes custom view filters, an optional custom query, and user defaults;
		 * similar to code in createStore, but used by column matrices that do not use store (but maybe should?)
		 * @param {Query} q
		 */
		applyLevelQuery: function(q) {
			var levelFilter = this.getFilter();
			if (levelFilter) {
				levelFilter.toQuery(q);
			}

			if (this.serverQuery) {
				q.importQuery(this.serverQuery);
			}

			this._applyUserDefaults(q);
		},

		/**
		 * Return limit to query result size at this level
		 * @return {number | undefined}
		 */
		getResultLimit() {
			return this.requestParameters?.["Fmt.Complete.Max"];
		},

		/**
		 * Return true if child level is null or it repeats
		 * @return {boolean}
		 */
		isLeafLevel() {
			return !this.childLevel || this.isRepeatLevel();
		},

		/**
		 * Return true if level repeats (childLevel will be the same)
		 * @return {boolean}
		 */
		isRepeatLevel() {
			return this.childLevel == this;
		},

		/**
		 * Return true if this is the root level
		 * @return {boolean}
		 */
		isRootLevel() {
			return this.id === ROOT_LEVEL;
		},

		onCreateStore: function(/*Store*/store, /*Boolean*/bConfigurable, /*QueryLevel*/ql) {
			// summary: extension point called when store created
		},

		/**
		 * This function is called when a default group appears in a selection dialog and requires a single child to select;
		 * a temporary node will already be created and pre-populated with Node Type and group value.
		 * @param {Object} defaultGroupItem - item from groupDefaultsStore
		 * @param {GroupNodes} groupStore - store containing groupNode
		 * @param {Node} groupNode - node for group created for defaultGroupItem
		 * @param {Any} groupValue - value of group
		 * @param {boolean} bEmpty - if true, then group is currently empty
		 */
		onDefaultGroup: function(defaultGroupItem, groupStore, groupNode, groupValue, bEmpty) {
			if (this.childLevel.bCanAggregate && this.qm.allowsSelections && this.qm.allowsSelections()) {
				// call extension point to determine if a node should be added to the default group
				var node = this.onDefaultGroupSelectionItem(groupStore, groupNode, groupValue, bEmpty, this.type, this.qm.cacheView);
				if (node) {
					var spec = this.groupSpec;
					var url = groupNode[groupStore.urlProperty];
					var h = spec.heading;

					// populate group attribute if no path
					if (!h.m_path) {
						xml.nodeCreateClientAttr(node, h.m_id, spec.type, url || groupValue);
					}

					// add this node to the group
					var refs = groupNode[groupStore.refsProperty];
					if (refs) {
						refs.push(node.$URL);
					} else {
						groupNode[groupStore.refsProperty] = [node.$URL];
					}
				}
			}
		},

		/**
		 * This function is called when a default group appears in a selection dialog. By default, if the group is empty,
		 * a temporary node will already be created and pre-populated with Node Type and group value. Override this
		 * function to fill in any other attributes on the node, such as its name, or to create a node in conditions when the
		 * group is not empty.
		 * @param {GroupNodes} groupStore - store containing groupNode
		 * @param {Node} groupNode - node for group containing selectionNode
		 * @param {Any} groupValue - value of group
		 * @param {boolean} bEmpty - if true, then group is currently empty
		 * @param {string} type - type of node to create
		 * @param {Cache} cache - cache to contain created node
		 * @return {Node|null} returns client node to add to group, if any
		 */
		onDefaultGroupSelectionItem: function(groupStore, groupNode, groupValue, bEmpty, type, cache) {
			return bEmpty ? cache.createClientNode(null, type) : null;
		},

		/**
		 * Return true if actions at this level should be disabled in current context
		 * @param {Context} ctx
		 * @param {Node} [node] - node is passed when checking access to row actions for the node
		 * @param {Node} [parent] - parent is passed when checking access to New actions under the parent row
		 * @return {boolean}
		 */
		shouldDisableActions: function(ctx, node, parent) {
			var f = this.actionsAccessFunction;
			if (!f && this.actions) {
				f = this.actionsAccessFunction = csi.resolveFunction(this.actions.accessAction, ctx.view);
			}

			if (!node && !parent) {
				return false;
			}

			return f && !f(ctx, node, parent);
		},

		/**
		 * Populate query with enforced user defaults, tracking both applied (enforced) user defaults and those not applied
		 * @param {Query} q
		 */
		_applyUserDefaults: function(q) {
			// for lower levels, including main level when grouped, it is possible that this is called multiple times - only compute once
			const {bMain, matrixSpecs, qm, skipEnforcedUserDefaults, skipUnenforcedUserDefaults, type} = this;
			var enforced = this.enforcedUserDefaults = [];
			var unenforced = this.unenforcedQueryObject = {};
			var store = bMain ? qm.get("querySpecStore") : null;
			var mainDefaults = {};

			function filterDefault(ud, matrixPath) {
				var key = ud.getMatrixKey(matrixPath);

				if (!matrixPath) {
					// store that user default is being applied from main class
					mainDefaults[ud.name] = 1;
				} else if (ud.filterSupersededBy.some((s) => mainDefaults[s])) {
					// check superseded defaults from matrix class to main class
					return false;
				}

				if (ud.enforced && skipEnforcedUserDefaults) {
					// data stores already query by enforced user defaults, so no need for client to do it
					return false;
				}

				if (ud.enforced || !store || !store.get(key)) {
					// track user defaults that will apply to level query
					enforced.push(ud);
					return true;
				}

				// track user defaults that are possible to change by user
				if (!skipUnenforcedUserDefaults) {
					unenforced[key] = ud.getQueryValues();
				}

				return false;
			}

			// apply user defaults from level class
			configPreferences.applyUserDefaultsToQuery(q, type, null, filterDefault);

			// also apply user defaults from matrix classes
			_.each(matrixSpecs, function(spec) {
				var ep = spec.matrixExtendedPath;
				if (ep) {
					configPreferences.applyUserDefaultsToQuery(q, ep.getEndType(), ep, filterDefault);
				}
			});
		},

		_hasCrewDependency: function() {
			// summary: returns true if this level has Dep.CrewUserAssignment or Dep.CrewRoleAssignment set
			var params = this.requestParameters;
			return params && (params["Dep.CrewUserAssignment"] || params["Dep.CrewRoleAssignment"]);
		}
	});

	_Level.ROOT_LEVEL = ROOT_LEVEL;

	return _Level;
});